<template>
  <div class="timeseries-chart" v-if="processed">
    <div class="info-card-container">
    <div class="info-card" v-for="row in processed" :key="row.country">
      <h6 class="title">{{row.country}}</h6>
      <responsive-wrapper class="bar-chart" v-slot="sizing" padding="10 36 24 10">
        <bar-chart
          v-bind="sizing"
          :data="row.data.map(datum2attr)"
          :x-domain="xDomain"
          :y-domain="row.yDomain"
          :x-format="xFormat"
          :y-format="row.interval >= 1000 ? yFormat : yFormat2"
          :interval="row.interval">
          <template v-slot="{xScale, yScale}">
            <text class="vg-text-label"
              :x="sizing.width"
              :y="sizing.height + 12"
              dx="0.5em"
              dy="0.7em"
              text-anchor="start">
              wks
            </text>
            <text class="china-annotation" v-if="row.country === 'China'"
              :y="yScale(9000)"
              text-anchor="start">
              <tspan :x="xScale(25)" dx="1em">Peak due to</tspan>
              <tspan :x="xScale(25)" dx="1em" dy="1em">methodology</tspan>
              <tspan :x="xScale(25)" dx="1em" dy="1em">change</tspan>
            </text>
          </template>
        </bar-chart>
      </responsive-wrapper>
      <table class="stat">
        <tbody>
          <tr class="new-cases">
            <td>+{{row.latest.new_cases | formatNumber}}</td>
            <th>new cases</th>
          </tr>
          <tr class="cases">
            <td>{{row.latest.cases | formatNumber}}</td>
            <th>confirmed cases</th>
          </tr>
          <tr class="recovered">
            <td>{{row.latest.recovered | formatNumber}}</td>
            <th>recovered</th>
          </tr>
          <tr class="deaths">
            <td>{{row.latest.deaths | formatNumber}}</td>
            <th>deaths</th>
          </tr>
          <tr class="active-cases">
            <th colspan="2">
              Over the past seven days,<br>there have been
              <span :class="row.growing ? 'growing' : 'falling'">more {{row.growing ? 'new cases' : 'recoveries'}}</span>
              than {{row.growing ? 'recoveries' : 'new cases'}}.</th>
          </tr>
        </tbody>
      </table>
    </div>
    </div>
    <!-- <p class="disclaimer" v-markdown="'* active cases = total cases - total recovered - total deaths'"></p> -->
  </div>
</template>

<script>
import { ResponsiveWrapper } from "vue-graphics/hocs";
import BarChart from "./barchart";

import { store } from "./wuhanData";

const N0 = 100;

const INTERVALS = [
  [200000, 40000],
  [100000, 20000],
  [40000, 10000],
  [20000, 4000],
  [10000, 2000],
  [5000, 1000],
  [2000, 400],
  [1000, 200],
  [500, 100],
  [200, 50],
];

export default {
  components: {
    ResponsiveWrapper,
    BarChart,
  },
  filters: {
    formatNumber(v) {
      return v.toLocaleString();
    },
  },
  props: ["attributes"],
  computed: {
    processed() {
      if (!store.timeseries || !store.sortBy) return null;
      const today = getToday();
      const items = this.attributes.value.items;
      const data = [...store.timeseries];
      const byCountry = {};
      data.forEach(row => {
        if (row.province) return;
        byCountry[row.country] = byCountry[row.country] || [];
        byCountry[row.country].push(row);
      });
      Object.entries(byCountry).forEach(([country, rows]) => {
        if (rows[0].cases < N0) {
          delete byCountry[country];
          return;
        }
        rows.reverse().forEach((row, i) => {
          row.new_cases = i === 0 ? row.cases : row.cases - rows[i - 1].cases;
        });
      });
      return items
        .map(item => {
          const rows = byCountry[item.country];
          const latest = rows[rows.length - 1];
          const minus7 = rows[rows.length - 8];
          const growing =
            latest.cases -
              latest.deaths -
              latest.recovered -
              (minus7.cases - minus7.deaths - minus7.recovered) >
            0;
          if (latest.date === today)
            latest.new_cases = rows[rows.length - 2].new_cases;
          return {
            country: item.country,
            data: rows.filter(row => row.cases >= N0),
            latest,
            minus7,
            yDomain: [0, +item.ymax],
            interval: getInterval(+item.ymax),
            growing,
          };
        })
        .sort((a, b) => {
          return b.yDomain[1] - a.yDomain[1] || b.latest.cases - a.latest.cases;
        })
        .sort(
          (a, b) => b.latest[store.sortBy.value] - a.latest[store.sortBy.value]
        );
    },
    xDomain() {
      const dateToday = new Date();
      const date1stCase = new Date("2/1/2020");
      const timeSince = dateToday - date1stCase;
      const daysSince = timeSince / (1000 * 3600 * 24);
      console.log(daysSince);
      return [...range(daysSince)];
    },
  },
  mounted() {
    this.$watch(
      () => store.rawData,
      function(data) {
        const ENGLISH_COUNT = [
          "one",
          "two",
          "three",
          "four",
          "five",
          "six",
          "seven",
          "eight",
          "nine",
          "ten",
        ];
        if (data) {
          const diamondRow = data.find(
            row => row.country === "Diamond Princess"
          );
          const skoreaRow = data.find(row => row.country === "South Korea");
          const italyRow = data.find(row => row.country === "Italy");
          const asiaRow = {
            cases: 0,
            deaths: 0,
            places: 0,
          };
          const outsideAsiaRow = {
            cases: 0,
            deaths: 0,
            places: 0,
          };
          data.forEach(row => {
            if (
              row.country === "China" ||
              row.country === "Diamond Princess" ||
              row.province
            )
              return;
            if (row.region === "Asia") {
              asiaRow.cases += row.cases;
              asiaRow.deaths += row.deaths;
              asiaRow.places++;
            } else {
              outsideAsiaRow.cases += row.cases;
              outsideAsiaRow.deaths += row.deaths;
              outsideAsiaRow.places++;
            }
          });
          const $diamondCases = document.querySelector(".diamond-cases");
          if ($diamondCases) $diamondCases.textContent = diamondRow.cases;
          const $diamondDeaths = document.querySelector(".diamond-deaths");
          if ($diamondDeaths)
            $diamondDeaths.textContent =
              diamondRow.deaths > 10
                ? diamondRow.deaths
                : ENGLISH_COUNT[diamondRow.deaths - 1];
          const $skoreaCases = document.querySelector(".skorea-cases");
          if ($skoreaCases)
            $skoreaCases.textContent = roundDown(
              skoreaRow.cases,
              100
            ).toLocaleString();
          const $italyCases = document.querySelector(".italy-cases");
          if ($italyCases)
            $italyCases.textContent = roundDown(
              italyRow.cases
            ).toLocaleString();
          const $asiaPlaces = document.querySelector(".asia-places");
          if ($asiaPlaces) $asiaPlaces.textContent = asiaRow.places;
          const $asiaCases = document.querySelector(".asia-cases");
          if ($asiaCases)
            $asiaCases.textContent = roundDown(asiaRow.cases).toLocaleString();
          const $outsideAsiaPlaces = document.querySelector(
            ".outside-asia-places"
          );
          if ($outsideAsiaPlaces)
            $outsideAsiaPlaces.textContent = outsideAsiaRow.places;
          const $outsideAsiaCases = document.querySelector(
            ".outside-asia-cases"
          );
          if ($outsideAsiaCases)
            $outsideAsiaCases.textContent = roundDown(
              outsideAsiaRow.cases
            ).toLocaleString();
          const $outsideChinaPlaces = document.querySelector(
            ".outside-china-places"
          );
          if ($outsideChinaPlaces)
            $outsideChinaPlaces.textContent =
              asiaRow.places + outsideAsiaRow.places;
        }
      },
      { immediate: true }
    );
  },
  methods: {
    datum2attr(d, i) {
      return {
        x: i,
        y: d.new_cases,
        class: "bar-" + i,
      };
    },
    xFormat(label) {
      return (label / 7).toFixed();
    },
    yFormat(label) {
      if (label === 0) return "";
      return (label / 1000).toFixed() + "K";
    },
    yFormat2(label) {
      if (label === 0) return "";
      return label.toFixed();
    },
  },
};

function getInterval(max) {
  return INTERVALS.filter(row => row[0] <= max)[0][1];
}

function getToday() {
  const d = new Date();
  d.setHours(d.getHours() + 8);
  return d.toISOString().slice(0, 10);
}

function* range(start, end, step = 1) {
  if (end == null) {
    end = start;
    start = 0;
  }
  let curr = start;
  while (curr < end) {
    yield curr;
    curr += step;
  }
}

function roundDown(n, base = 1000) {
  return Math.floor(n / base) * base;
}
</script>

<style lang="scss">
.timeseries-chart {
  .info-card-container {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
  }

  .info-card {
    flex-basis: 100%;
    flex-shrink: 0;
    background-color: $screen;
    border: 5px solid white;
    padding: 15px;
    box-sizing: border-box;
    font-family: $font_CuratorRegular;
    font-size: 15px;

    @include breakpoint(540px) {
      flex-basis: 50%;
    }

    @include breakpoint($d_medium) {
      flex-basis: 33%;
    }

    @include breakpoint($d_normal) {
      flex-basis: 25%;
    }

    .title {
      font-family: $font_CuratorBold;
      font-size: 18px;
      color: $black;
    }

    .bar-chart {
      width: 100%;
      height: 150px;
      margin: 30px 0;
    }

    .china-annotation {
      font-size: 10px;
    }

    .stat {
      max-width: 210px;
      margin: 0 auto;
      border-collapse: separate;
      border-spacing: 12px 2px;

      td {
        font-family: $font-CuratorBold;
        text-align: right;
        vertical-align: top;
        // width: 30%;
      }

      th {
        width: 100%;
      }

      .growing {
        font-family: $font-CuratorBold;
        color: rgb(212, 136, 61);
      }

      .falling {
        font-family: $font-CuratorBold;
        color: rgb(2, 199, 191);
      }
    }

    tr.cases td {
      color: rgb(212, 136, 61);
    }

    tr.recovered td {
      color: rgb(2, 199, 191);
    }

    tr.deaths td {
      color: rgb(149, 35, 112);
    }

    tr.active-cases th {
      padding-top: 20px;
    }
  }

  .disclaimer {
    font-family: $font_CuratorRegular;
    font-size: 12px;
    padding-left: 1em;
  }
}
</style>
